import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';
import { secureUpload } from '../../../services/s3UploadService';

const { FiImage, FiCamera, FiLoader, FiCheck, FiSave } = FiIcons;

const PROVIDERS_TABLE = 'service_providers_1777741098';

const Input = ({ className, ...props }) => (
  <input className={`w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-colors ${className || ''}`} {...props} />
);

const TextArea = ({ className, ...props }) => (
  <textarea className={`w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-colors resize-none ${className || ''}`} {...props} />
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{label}</label>
    {children}
    {hint && <p className="text-xs text-text-muted">{hint}</p>}
  </div>
);

const SaveButton = ({ saving, onClick }) => (
  <button 
    onClick={onClick}
    disabled={saving}
    className="px-6 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-md flex items-center gap-2 disabled:opacity-70"
  >
    {saving ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
    {saving ? 'Saving...' : 'Save Changes'}
  </button>
);

const SavedBadge = ({ show }) => (
  <div className={`flex items-center gap-2 text-green-600 text-sm font-bold transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}>
    <SafeIcon icon={FiCheck} className="w-4 h-4" /> Saved Successfully
  </div>
);

const BrandingSection = () => {
  const user = getSession();
  const ownerId = user?.id || 'anonymous';
  const [providerRecord, setProviderRecord] = useState(null);
  const [form, setForm] = useState({
    banner_url: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?w=800',
    brand_color: '#1E3A8A',
    tagline: 'Compassionate care in every detail.',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const res = await writeFetch(`read/${PROVIDERS_TABLE}?owner_id=${ownerId}&limit=1`);
        const data = await res.json();
        if (data.status === 'success' && data.data?.length > 0) {
          const rec = data.data[0];
          setProviderRecord(rec);
          setForm({
            banner_url: rec.banner_url || form.banner_url,
            brand_color: rec.brand_color || form.brand_color,
            tagline: rec.tagline || form.tagline,
          });
        }
      } catch {}
    };
    fetchRecord();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...providerRecord,
      owner_id: ownerId,
      banner_url: form.banner_url,
      brand_color: form.brand_color,
      tagline: form.tagline,
      updated_at: new Date().toISOString()
    };

    try {
      if (providerRecord?.id) {
        await writeFetch(`update/${PROVIDERS_TABLE}/${providerRecord.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await writeFetch(`create/${PROVIDERS_TABLE}`, { method: 'POST', body: JSON.stringify({ ...payload, id: crypto.randomUUID() }) });
      }
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const { s3Url } = await secureUpload({
        file,
        ownerId: ownerId,
        category: 'merchant-banner',
        onProgress: (pct) => setUploadProgress(pct)
      });
      set('banner_url', s3Url);
    } catch (err) {
      alert('Failed to upload image. Try using a URL instead.');
    }
    
    setIsUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Shop Banner</label>
        <div className="relative h-48 rounded-[28px] overflow-hidden border-2 border-brand-dark bg-brand-light group">
          {form.banner_url ? (
            <img src={form.banner_url} alt="Banner" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
             <div className="w-full h-full flex items-center justify-center text-text-muted"><SafeIcon icon={FiImage} className="w-8 h-8" /></div>
          )}
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-5 py-2.5 bg-white text-accent-navy rounded-xl text-xs font-bold shadow-md flex items-center justify-center gap-2 hover:bg-brand-light transition-colors disabled:opacity-70"
            >
              {isUploading ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  <span>{uploadProgress}%</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiCamera} className="w-4 h-4" /> Change Banner
                </>
              )}
            </button>
          </div>
        </div>
        <input 
          type="file" 
          accept="image/*" 
          ref={fileInputRef} 
          onChange={handleBannerUpload} 
          className="hidden" 
        />
        <Input 
          placeholder="Or paste image URL..." 
          value={form.banner_url} 
          onChange={e => set('banner_url', e.target.value)} 
          className="mt-4 w-full" 
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Brand Tagline" hint="A short, memorable phrase displayed on your profile.">
          <Input value={form.tagline} onChange={e => set('tagline', e.target.value)} placeholder="A short, memorable phrase..." />
        </Field>
        <Field label="Primary Brand Color" hint="Used for buttons and accents on your shop page.">
          <div className="flex items-center gap-3">
             <input type="color" value={form.brand_color} onChange={e => set('brand_color', e.target.value)} className="w-12 h-12 rounded-xl cursor-pointer border-2 border-brand-dark p-0.5 bg-white" />
             <Input value={form.brand_color} onChange={e => set('brand_color', e.target.value)} className="flex-1 uppercase font-mono" />
          </div>
        </Field>
      </div>

      <div className="flex items-center gap-4 pt-4 border-t border-brand-light">
        <SaveButton saving={saving} onClick={handleSave} />
        <SavedBadge show={saved} />
      </div>
    </div>
  );
};

const ProfileSection = () => {
  const user = getSession();
  const ownerId = user?.id || 'anonymous';
  const [providerRecord, setProviderRecord] = useState(null);
  const [form, setForm] = useState({
    name: '',
    type: 'Funeral Home',
    phone: '',
    address: '',
    description: '',
    whatsapp_enabled: false,
    whatsapp: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const res = await writeFetch(`read/${PROVIDERS_TABLE}?owner_id=${ownerId}&limit=1`);
        const data = await res.json();
        if (data.status === 'success' && data.data?.length > 0) {
          const rec = data.data[0];
          setProviderRecord(rec);
          setForm({
            name: rec.name || '',
            type: rec.type || 'Funeral Home',
            phone: rec.phone || '',
            address: rec.address || '',
            description: rec.description || '',
            whatsapp_enabled: !!rec.whatsapp_enabled,
            whatsapp: rec.whatsapp || ''
          });
        }
      } catch {}
    };
    fetchRecord();
  }, [ownerId]);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...providerRecord,
      owner_id: ownerId,
      name: form.name,
      type: form.type,
      phone: form.phone,
      address: form.address,
      description: form.description,
      whatsapp_enabled: form.whatsapp_enabled,
      whatsapp: form.whatsapp,
      updated_at: new Date().toISOString()
    };

    try {
      if (providerRecord?.id) {
        await writeFetch(`update/${PROVIDERS_TABLE}/${providerRecord.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await writeFetch(`create/${PROVIDERS_TABLE}`, { method: 'POST', body: JSON.stringify({ ...payload, id: crypto.randomUUID() }) });
      }
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Business Name">
          <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Serenity Funeral Home" />
        </Field>
        <Field label="Business Type">
          <select 
            value={form.type} 
            onChange={e => set('type', e.target.value)}
            className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm"
          >
            <option value="Funeral Home">Funeral Home</option>
            <option value="Cremation Service">Cremation Service</option>
            <option value="Memorial Chapel">Memorial Chapel</option>
            <option value="Grief Counselor">Grief Counselor</option>
            <option value="Cemetery">Cemetery</option>
          </select>
        </Field>
      </div>
      
      <Field label="Business Description">
        <TextArea rows="4" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe your services..." />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Primary Phone">
          <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" />
        </Field>
        <Field label="Address">
          <Input value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St, City, ST" />
        </Field>
      </div>

      <div className="p-5 bg-brand-light border border-brand-dark rounded-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-bold text-accent-navy text-sm">WhatsApp Integration</h4>
            <p className="text-xs text-text-muted">Allow customers to contact you via WhatsApp</p>
          </div>
          <button 
            type="button" 
            onClick={() => set('whatsapp_enabled', !form.whatsapp_enabled)}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 shadow-inner ${form.whatsapp_enabled ? 'bg-green-500' : 'bg-gray-200'}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.whatsapp_enabled ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
        {form.whatsapp_enabled && (
          <Field label="WhatsApp Number (with country code)">
            <Input value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="+1234567890" />
          </Field>
        )}
      </div>

      <div className="flex items-center gap-4 pt-4 border-t border-brand-light">
        <SaveButton saving={saving} onClick={handleSave} />
        <SavedBadge show={saved} />
      </div>
    </div>
  );
};

const PoliciesSection = () => {
  const user = getSession();
  const ownerId = user?.id || 'anonymous';
  const [providerRecord, setProviderRecord] = useState(null);
  const [form, setForm] = useState({
    refund_policy: '',
    cancellation_policy: '',
    delivery_policy: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const res = await writeFetch(`read/${PROVIDERS_TABLE}?owner_id=${ownerId}&limit=1`);
        const data = await res.json();
        if (data.status === 'success' && data.data?.length > 0) {
          const rec = data.data[0];
          setProviderRecord(rec);
          setForm({
            refund_policy: rec.refund_policy || '',
            cancellation_policy: rec.cancellation_policy || '',
            delivery_policy: rec.delivery_policy || ''
          });
        }
      } catch {}
    };
    fetchRecord();
  }, [ownerId]);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...providerRecord,
      owner_id: ownerId,
      refund_policy: form.refund_policy,
      cancellation_policy: form.cancellation_policy,
      delivery_policy: form.delivery_policy,
      updated_at: new Date().toISOString()
    };

    try {
      if (providerRecord?.id) {
        await writeFetch(`update/${PROVIDERS_TABLE}/${providerRecord.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await writeFetch(`create/${PROVIDERS_TABLE}`, { method: 'POST', body: JSON.stringify({ ...payload, id: crypto.randomUUID() }) });
      }
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <Field label="Refund Policy" hint="Explain your terms for refunds.">
        <TextArea rows="3" value={form.refund_policy} onChange={e => set('refund_policy', e.target.value)} placeholder="No refunds after 24 hours..." />
      </Field>
      <Field label="Cancellation Policy" hint="Explain your terms for cancellations.">
        <TextArea rows="3" value={form.cancellation_policy} onChange={e => set('cancellation_policy', e.target.value)} placeholder="Cancellations must be made 48 hours in advance..." />
      </Field>
      <Field label="Delivery / Fulfillment Policy" hint="Explain how and when services or products are delivered.">
        <TextArea rows="3" value={form.delivery_policy} onChange={e => set('delivery_policy', e.target.value)} placeholder="Services will be fulfilled at the agreed location..." />
      </Field>
      <div className="flex items-center gap-4 pt-4 border-t border-brand-light">
        <SaveButton saving={saving} onClick={handleSave} />
        <SavedBadge show={saved} />
      </div>
    </div>
  );
};

const MerchantSettingsTab = () => {
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="bg-white rounded-[32px] border border-brand-dark p-8">
      <div className="flex items-center justify-between pb-5 border-b border-brand-dark mb-8">
        <div>
          <h3 className="font-serif text-2xl text-accent-navy">Shop Settings</h3>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">Manage your business profile and policies</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-64 shrink-0 flex flex-col gap-2">
          {['profile', 'branding', 'policies'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-4 rounded-2xl text-left text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === tab 
                  ? 'bg-accent-navy text-white shadow-md'
                  : 'bg-brand-light text-text-muted hover:bg-brand-light/70 hover:text-accent-navy'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex-1 bg-white">
          {activeTab === 'profile' && <ProfileSection />}
          {activeTab === 'branding' && <BrandingSection />}
          {activeTab === 'policies' && <PoliciesSection />}
        </div>
      </div>
    </div>
  );
};

export default MerchantSettingsTab;